// =============================================================================
// TESTIMONIAL SECTION COMPONENT (V2 - NORMALIZED)
// =============================================================================

import React from 'react';
import { Star, Quote } from 'lucide-react';
import { layoutToClasses, displayToStyles } from '@/types/layout';
import type { ContentComponent } from '@/types';
import type { TestimonialComponent } from '@/types/components';

interface TestimonialSectionProps {
  component: ContentComponent;
}

/**
 * Type guard for new TestimonialComponent format
 */
function isNewTestimonialFormat(component: ContentComponent): boolean {
  return (
    component.type === 'testimonial' &&
    'items' in component &&
    Array.isArray((component as any).items)
  );
}

/**
 * Testimonial Section Component
 */
export const TestimonialSection: React.FC<TestimonialSectionProps> = ({ component }) => {
  if (isNewTestimonialFormat(component)) {
    return <TestimonialSectionNew component={component as unknown as any} />;
  }
  return <TestimonialSectionLegacy component={component} />;
};

/**
 * Render stars for rating
 */
const renderStars = (rating: number) => {
  return Array.from({ length: 5 }, (_, index) => (
    <Star
      key={index}
      size={16}
      className={`star ${index < rating ? 'filled' : 'empty'}`}
    />
  ));
};

/**
 * New format Testimonial Section
 */
const TestimonialSectionNew: React.FC<{ component: TestimonialComponent }> = ({ component }) => {
  const { id, content, items, layout, display } = component;
  
  const layoutClasses = layoutToClasses(layout);
  const displayStyles = displayToStyles(display);
  
  return (
    <section id={id} className={`content-section testimonial-section ${layoutClasses}`} style={displayStyles}>
      <div className="container">
        {content.title && (
          <div className="section-header">
            <h2>{content.title}</h2>
            {content.description && <p>{content.description}</p>}
          </div>
        )}
        
        <div className={`testimonial-grid grid-cols-${layout.columns || 3}`}>
          {items.map((testimonial, index) => (
            <div key={testimonial.id || index} className="testimonial-card">
              <div className="testimonial-quote">
                <Quote size={24} className="quote-icon" />
              </div>
              
              <div className="testimonial-content">
                <p>"{testimonial.body}"</p>
              </div>
              
              {testimonial.rating && (
                <div className="testimonial-rating">
                  {renderStars(testimonial.rating)}
                </div>
              )}
              
              <div className="testimonial-author">
                <div className="author-info">
                  {testimonial.avatar && (
                    <div className="author-avatar">
                      <img src={testimonial.avatar} alt={testimonial.name} loading="lazy" />
                    </div>
                  )}
                  
                  <div className="author-details">
                    <div className="author-name">{testimonial.name}</div>
                    <div className="author-title">
                      {testimonial.role}
                      {testimonial.company && (
                        <>
                          {' at '}
                          <span className="author-company">{testimonial.company}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Legacy format Testimonial Section
 */
const TestimonialSectionLegacy: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const testimonials = metadata?.testimonials || [];
  const columns = metadata?.columns || 3;
  
  return (
    <section id={id} className="content-section testimonial-section">
      <div className="container">
        {title && (
          <div className="section-header">
            <h2>{title}</h2>
            {content && <p>{content}</p>}
          </div>
        )}
        
        <div className={`testimonial-grid grid-cols-${columns}`}>
          {testimonials.map((testimonial: any, index: number) => (
            <div key={index} className="testimonial-card">
              <div className="testimonial-quote">
                <Quote size={24} className="quote-icon" />
              </div>
              
              <div className="testimonial-content">
                <p>"{testimonial.body}"</p>
              </div>
              
              {testimonial.rating && (
                <div className="testimonial-rating">
                  {renderStars(testimonial.rating)}
                </div>
              )}
              
              <div className="testimonial-author">
                <div className="author-info">
                  {testimonial.avatar && (
                    <div className="author-avatar">
                      <img src={testimonial.avatar} alt={testimonial.name} loading="lazy" />
                    </div>
                  )}
                  
                  <div className="author-details">
                    <div className="author-name">{testimonial.name}</div>
                    <div className="author-title">
                      {testimonial.role}
                      {testimonial.company && (
                        <>
                          {' at '}
                          <span className="author-company">{testimonial.company}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export { TestimonialSectionNew, TestimonialSectionLegacy };

